import React, { useState, useEffect } from 'react';
import "../css/Profile.css";

function Profile() {
  // User details state
  const [user, setUser] = useState({
    name: "John Doe",
    email: "john.doe@example.com",
    joinDate: "Jan 15, 2025",
    role: "Premium Member"
  });

  // Password modal state
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordData, setPasswordData] = useState({
    oldPassword: "",
    newPassword: "",
    confirmPassword: ""
  });
  const [passwordStrength, setPasswordStrength] = useState(0); // 0: none, 1: weak, 2: medium, 3: strong
  const [errorMessage, setErrorMessage] = useState("");

  // Lock body scroll when modal is open
  useEffect(() => {
    if (showPasswordModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showPasswordModal]);

  // Handle input changes
  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData({
      ...passwordData,
      [name]: value
    });

    // Calculate password strength for new password
    if (name === "newPassword") {
      calculatePasswordStrength(value);
    }
  };

  // Password strength calculator
  const calculatePasswordStrength = (password) => {
    if (!password) {
      setPasswordStrength(0);
      return;
    }

    let strength = 0;
    
    // Length check
    if (password.length >= 8) strength += 1;
    
    // Complexity checks
    if (/[A-Z]/.test(password)) strength += 1;
    if (/[0-9]/.test(password)) strength += 1;
    if (/[^A-Za-z0-9]/.test(password)) strength += 1;
    
    // Normalize to 0-3 scale
    setPasswordStrength(Math.min(3, Math.floor(strength * 3 / 4)));
  };

  // Submit password change
  const handleSubmitPassword = (e) => {
    e.preventDefault();
    setErrorMessage("");

    // Basic validation
    if (!passwordData.oldPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
      setErrorMessage("All fields are required");
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setErrorMessage("New passwords don't match");
      return;
    }

    if (passwordStrength < 2) {
      setErrorMessage("Please use a stronger password");
      return;
    }

    // Here you would typically call an API to change the password
    // For demo purposes, we'll just close the modal and reset
    alert("Password updated successfully!");
    setShowPasswordModal(false);
    setPasswordData({
      oldPassword: "",
      newPassword: "",
      confirmPassword: ""
    });
  };

  // Get strength class and label
  const getStrengthInfo = () => {
    switch (passwordStrength) {
      case 1:
        return { class: "weak", label: "Weak" };
      case 2:
        return { class: "medium", label: "Medium" };
      case 3:
        return { class: "strong", label: "Strong" };
      default:
        return { class: "", label: "" };
    }
  };

  const strengthInfo = getStrengthInfo();

  // Close modal when clicking outside
  const handleOverlayClick = (e) => {
    if (e.target.className === 'modal-overlay') {
      setShowPasswordModal(false);
    }
  };

  return (
    <div className="profile-container">
      <div className="profile-card">
        <div className="profile-header">
          <h1>User Profile</h1>
        </div>
        
        <div className="profile-details">
          <div className="profile-avatar">
            {/* User initial as avatar */}
            <div className="avatar-circle">
              {user.name.charAt(0)}
            </div>
          </div>
          
          <div className="user-info">
            <h2>{user.name}</h2>
            <p><span>Email:</span> {user.email}</p>
            <p><span>Member Since:</span> {user.joinDate}</p>
            <p><span>Account Type:</span> {user.role}</p>
            
            <button 
              className="change-password-btn"
              onClick={() => setShowPasswordModal(true)}
            >
              Change Password
            </button>
          </div>
        </div>
      </div>

      {/* Password Change Modal */}
      {showPasswordModal && (
        <div className="modal-overlay" onClick={handleOverlayClick}>
          <div className="password-modal">
            <div className="modal-header">
              <h2>Change Password</h2>
              <button 
                className="close-modal"
                onClick={() => setShowPasswordModal(false)}
                aria-label="Close modal"
              >
                &times;
              </button>
            </div>
            
            <form onSubmit={handleSubmitPassword}>
              <div className="form-group">
                <label htmlFor="oldPassword">Current Password</label>
                <input
                  type="password"
                  id="oldPassword"
                  name="oldPassword"
                  value={passwordData.oldPassword}
                  onChange={handlePasswordChange}
                  autoComplete="current-password"
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="newPassword">New Password</label>
                <input
                  type="password"
                  id="newPassword"
                  name="newPassword"
                  value={passwordData.newPassword}
                  onChange={handlePasswordChange}
                  autoComplete="new-password"
                />
                
                {passwordData.newPassword && (
                  <div className="password-strength">
                    <div className="strength-meter">
                      <div 
                        className={`strength-progress ${strengthInfo.class}`}
                        style={{ width: `${(passwordStrength / 3) * 100}%` }}
                      ></div>
                    </div>
                    <span className={`strength-label ${strengthInfo.class}`}>
                      {strengthInfo.label}
                    </span>
                  </div>
                )}
              </div>
              
              <div className="form-group">
                <label htmlFor="confirmPassword">Confirm New Password</label>
                <input
                  type="password"
                  id="confirmPassword"
                  name="confirmPassword"
                  value={passwordData.confirmPassword}
                  onChange={handlePasswordChange}
                  autoComplete="new-password"
                />
              </div>
              
              {errorMessage && (
                <div className="error-message">{errorMessage}</div>
              )}
              
              <div className="form-actions">
                <button 
                  type="button" 
                  className="cancel-btn"
                  onClick={() => setShowPasswordModal(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="submit-btn">
                  Update Password
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Profile;